import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import { styled } from 'styled-components';

import type { DecisivePlayerPickerMode } from '@sorare/core/src/__generated__/globalTypes';
import { DecisivePlayerPickerTaskAppearanceStatus } from '@sorare/core/src/__generated__/globalTypes';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { missionBackground } from '@sorare/core/src/components/missions/style';
import { glossary } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { DecisivePickerSlot } from '#src/components/missions/picker/DecisivePickerSlot/index.tsx';
import { TaskResultsPreClaimStoryTemplate } from '#src/components/missions/stories/TaskResultsPreClaimStoryTemplate/index.tsx';

import type {
  ClaimYourRewardsStory_decisivePlayerPickerTask,
  ClaimYourRewardsStory_decisivePlayerPickerTaskAppearance,
  ClaimYourRewardsStory_taskAppearance,
  ClaimYourRewardsStory_taskInterface,
} from './__generated__/index.graphql.ts';

const Root = styled(Horizontal).attrs({ center: true })`
  position: relative;
  ${missionBackground}
  height: 100%;
  width: 100%;
  padding: var(--double-unit);

  @media ${laptopAndAbove} {
    min-height: min(calc(90 * var(--unit)), 100vh);
  }
`;

const ButtonsWrapper = styled(Vertical).attrs({ center: true })`
  position: absolute;
  bottom: var(--quadruple-unit);
  left: var(--double-unit);
  right: var(--double-unit);

  opacity: 0;
  animation: ${fadeIn} 0.65s cubic-bezier(0.75, 0.07, 0.23, 0.89) forwards;
  animation-delay: 1.35s;
`;

type Props = {
  taskAppearances: (
    | ClaimYourRewardsStory_decisivePlayerPickerTaskAppearance
    | undefined
  )[];
  mode: DecisivePlayerPickerMode;
  onContinue: () => void;
};

export const ClaimYourRewardsStory = ({
  taskAppearances,
  mode,
  onContinue,
}: Props) => {
  return (
    <Root
      key="decisive-player-picker-story"
      style={
        {
          '--card-avatar-size': 'calc(var(--unit) * 8)',
          '--rgb-color': 'var(--c-rgb-green-400)',
          '--opacity': '0.4',
        } as CSSProperties
      }
    >
      <TaskResultsPreClaimStoryTemplate
        featuredContent={
          <Horizontal gap={4}>
            {taskAppearances.map((taskAppearance, index) => {
              return (
                <DecisivePickerSlot
                  key={taskAppearance?.id || `slot-${index}`}
                  taskAppearance={taskAppearance}
                  isSelected={false}
                  index={index}
                  showAction
                  showLockedOverlay={false}
                  mode={mode}
                />
              );
            })}
          </Horizontal>
        }
        title={
          <FormattedMessage defaultMessage="You nailed your decisive picks!" />
        }
        subtitle={
          <FormattedMessage
            defaultMessage="You correctly predicted {correct} out of {total, plural, one {# player} other {# players}} to get a decisive action."
            values={{
              correct: taskAppearances.filter(
                a =>
                  a?.status === DecisivePlayerPickerTaskAppearanceStatus.SUCCESS
              ).length,
              total: taskAppearances.length,
            }}
          />
        }
      />
      <ButtonsWrapper>
        <Button
          size="large"
          onClick={onContinue}
          color="quaternary"
          disableDebounce
        >
          <FormattedMessage {...glossary.continue} />
        </Button>
      </ButtonsWrapper>
    </Root>
  );
};

const fragment_decisivePlayerPickerTaskAppearance = gql`
  fragment ClaimYourRewardsStory_decisivePlayerPickerTaskAppearance on DecisivePlayerPickerTaskAppearance {
    id
    status
    ...DecisivePickerSlot_taskAppearance
  }
  ${DecisivePickerSlot.fragments.taskAppearance}
` as TypedDocumentNode<ClaimYourRewardsStory_decisivePlayerPickerTaskAppearance>;

const fragment_taskAppearance = gql`
  fragment ClaimYourRewardsStory_taskAppearance on TaskAppearanceInterface {
    id
    ... on DecisivePlayerPickerTaskAppearance {
      id
      ...ClaimYourRewardsStory_decisivePlayerPickerTaskAppearance
    }
    ...DecisivePickerSlot_taskAppearance
  }
  ${fragment_decisivePlayerPickerTaskAppearance}
  ${DecisivePickerSlot.fragments.taskAppearance}
` as TypedDocumentNode<ClaimYourRewardsStory_taskAppearance>;

const fragment_decisivePlayerPickerTask = gql`
  fragment ClaimYourRewardsStory_decisivePlayerPickerTask on DecisivePlayerPickerTask {
    id
    taskAppearances {
      id
      ...ClaimYourRewardsStory_taskAppearance
    }
  }
  ${fragment_taskAppearance}
` as TypedDocumentNode<ClaimYourRewardsStory_decisivePlayerPickerTask>;

ClaimYourRewardsStory.fragments = {
  decisivePlayerPickerTask: fragment_decisivePlayerPickerTask,
  taskInterface: gql`
    fragment ClaimYourRewardsStory_taskInterface on TaskInterface {
      id
      ... on DecisivePlayerPickerTask {
        id
        ...ClaimYourRewardsStory_decisivePlayerPickerTask
      }
    }
    ${fragment_decisivePlayerPickerTask}
  ` as TypedDocumentNode<ClaimYourRewardsStory_taskInterface>,
};
